import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsPage = () => {
  const lastUpdated = "February 16, 2026";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">H</span>
              </div>
              <span className="font-serif text-xl font-medium text-primary">HomeClass</span>
            </Link>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary">
                Terms of Service
              </h1>
              <p className="text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none" data-testid="terms-content">
            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground mb-4">
                Welcome to HomeClass. By accessing or using our AI-powered quiz generation platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
              </p>
              <p className="text-muted-foreground">
                These Terms apply to all visitors, users, and others who access or use the Service, including teachers, parents, and students.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground mb-4">
                HomeClass provides an AI-powered platform that enables teachers and homeschool parents to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Upload or input lesson content</li>
                <li>Generate quizzes automatically using artificial intelligence</li>
                <li>Manage student accounts and assignments</li>
                <li>Track student progress and scores</li>
                <li>Export quizzes in various formats</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">3. Account Registration</h2>
              <p className="text-muted-foreground mb-4">
                To use certain features of the Service, you must register for an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                You must be at least 18 years old to create a teacher/parent account. Student accounts are created by teachers/parents and are subject to parental consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">4. Subscription and Payments</h2>
              
              <h3 className="font-medium text-foreground mb-2">4.1 Plans and Pricing</h3>
              <p className="text-muted-foreground mb-4">
                HomeClass offers various subscription plans with different features and limits. Current pricing is displayed on our pricing page and may be updated from time to time.
              </p>

              <h3 className="font-medium text-foreground mb-2">4.2 Billing</h3>
              <p className="text-muted-foreground mb-4">
                Subscription fees are billed in advance on a monthly basis. You authorize us to charge your payment method for all fees incurred.
              </p>

              <h3 className="font-medium text-foreground mb-2">4.3 Top-Up Credits</h3>
              <p className="text-muted-foreground mb-4">
                Quiz credits purchased as top-ups are non-refundable and do not expire. They can be used in addition to your monthly subscription quota.
              </p>

              <h3 className="font-medium text-foreground mb-2">4.4 Refunds</h3>
              <p className="text-muted-foreground">
                We offer a 30-day money-back guarantee for subscription payments. Refund requests must be made within 30 days of the initial payment. Top-up credits are non-refundable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">5. Acceptable Use</h2>
              <p className="text-muted-foreground mb-4">You agree NOT to use the Service to:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Upload content that infringes intellectual property rights</li>
                <li>Upload harmful, offensive, or inappropriate content</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the Service for any illegal purpose</li>
                <li>Share your account credentials with others</li>
                <li>Circumvent usage limits or subscription restrictions</li>
                <li>Resell or redistribute generated content commercially without permission</li>
                <li>Use automated means to access the Service (bots, scrapers)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">6. Content Ownership</h2>
              
              <h3 className="font-medium text-foreground mb-2">6.1 Your Content</h3>
              <p className="text-muted-foreground mb-4">
                You retain ownership of any content you upload to HomeClass (lesson materials, passages, etc.). By uploading content, you grant us a license to process it for the purpose of generating quizzes.
              </p>

              <h3 className="font-medium text-foreground mb-2">6.2 Generated Content</h3>
              <p className="text-muted-foreground mb-4">
                Quizzes generated by our AI are provided for your educational use. You may use, modify, and distribute generated quizzes for non-commercial educational purposes.
              </p>

              <h3 className="font-medium text-foreground mb-2">6.3 Our Content</h3>
              <p className="text-muted-foreground">
                The Service, including its design, features, and pre-built templates, is owned by HomeClass and protected by intellectual property laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">7. AI-Generated Content Disclaimer</h2>
              <p className="text-muted-foreground mb-4">
                Our Service uses artificial intelligence to generate quiz questions. While we strive for accuracy:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>AI-generated content may contain errors or inaccuracies</li>
                <li>You are responsible for reviewing and verifying generated content</li>
                <li>We recommend editing questions before use with students</li>
                <li>We do not guarantee the educational appropriateness of generated content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">8. Privacy</h2>
              <p className="text-muted-foreground">
                Your use of the Service is also governed by our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, which describes how we collect, use, and protect your information. By using the Service, you consent to our data practices as described in the Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">9. Termination</h2>
              <p className="text-muted-foreground mb-4">
                We may terminate or suspend your account immediately, without prior notice, if you breach these Terms. You may terminate your account at any time by contacting us.
              </p>
              <p className="text-muted-foreground">
                Upon termination, your right to use the Service will immediately cease. We may delete your data in accordance with our data retention policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">10. Disclaimers</h2>
              <p className="text-muted-foreground mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>The Service will be uninterrupted or error-free</li>
                <li>Results obtained from the Service will be accurate</li>
                <li>The Service will meet your specific requirements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">11. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, HOMECLASS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">12. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless HomeClass and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising out of your use of the Service or violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">13. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms at any time. We will notify you of significant changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">14. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">15. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-secondary/50 rounded-lg">
                <p className="text-foreground font-medium">HomeClass Support</p>
                <p className="text-muted-foreground">
                  Email:{' '}
                  <a 
                    href="mailto:homeclassem@gmail.com" 
                    className="text-primary hover:underline"
                  >
                    homeclassem@gmail.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center justify-between w-full gap-4">
              <p className="text-sm text-muted-foreground">
                © 2026 HomeClass. All rights reserved.
              </p>
              <div className="flex gap-6">
                <Link to="/terms" className="text-sm text-primary font-medium">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              For support, contact us at{' '}
              <a 
                href="mailto:homeclassem@gmail.com" 
                className="text-primary hover:underline"
              >
                homeclassem@gmail.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
